import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, CheckCircle2, Loader2, Building2, UserCircle2, CalendarPlus, ShieldCheck, RefreshCw, Euro, Users, Plus } from "lucide-react";

const LOGO = "/logo-aae.png";
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPENSE_CATEGORIES = [
  { v: "transport_train",    label: "Train (Déplacement)" },
  { v: "transport_avion",    label: "Avion (Déplacement)" },
  { v: "transport_taxi",     label: "Taxi (Déplacement)" },
  { v: "transport_parking",  label: "Parking (Déplacement)" },
  { v: "transport_peage",    label: "Péage (Déplacement)" },
  { v: "transport_voiture",  label: "Voiture perso (Déplacement)" },
  { v: "transport_location", label: "Voiture location (Déplacement)" },
  { v: "hebergement",        label: "Hébergement" },
  { v: "restauration",       label: "Restauration" },
  { v: "autre",              label: "Autre" },
];

const EMPTY_INTERMITTENT = {
  intermittent_id: "",
  intermittent_name_input: "",
  role: "",
  role_type: "technicien",
  cachet_count: 1,
  hours_cachet: 0,
  start_time: "",              // heure de prise de poste (portail entreprise : par ligne)
  cost_ht_client: 0,           // coût HT à facturer pour cet intermittent
  expenses_expected: false,    // Oui/Non pour cet intermittent — défaut Non
  fees_included: true,         // Imputation (conditionnel si expenses_expected)
  notes: "",                   // Notes complémentaires spécifiques à cet intermittent
};

const EMPTY = {
  client_id: "", company_name: "",
  contact_first_name: "", contact_last_name: "", contact_email: "", contact_phone: "", contact_role: "",
  event_name: "", event_location: "",
  start_date: "", end_date: "",
  start_time: "",                   // HH:MM prise de poste
  // Multi-intermittents (portail entreprise) — même événement/même dates
  intermittents: [{ ...EMPTY_INTERMITTENT }],
  // Legacy (portail intermittent / lien libre) : cachet_count/hours_cachet racines
  cachet_count: 1,
  hours_cachet: 0,
  intermittent_id: "",
  role: "",
  role_type: "technicien",
  cost_ht_client: 0,
  fees_included: true,
  expenses_expected: false, // Oui/Non — défaut Non
  contract_expenses: [],           // [{ title, category, amount }]
  notes: "",
};

// Format d'affichage pour datalist intermittent : "NOM Prénom"
const fmtIntermittent = (im) => `${(im?.last_name || "").toUpperCase()} ${im?.first_name || ""}`.trim();

// Fusionne le contact principal + contacts secondaires d'un client → liste unifiée
const gatherContacts = (client) => {
  if (!client) return [];
  const all = [];
  const primary = {
    first_name: client.contact_first_name || "",
    last_name: client.contact_last_name || "",
    email: client.contact_email || "",
    phone: client.contact_phone || "",
    role: client.contact_role || "",
  };
  if (primary.first_name || primary.last_name || primary.email) all.push(primary);
  for (const c of (client.contacts || [])) {
    if (c && (c.first_name || c.last_name || c.email)) all.push(c);
  }
  // dédup par (email || nom-prénom)
  const seen = new Set();
  return all.filter(c => {
    const k = (c.email || `${c.last_name}-${c.first_name}`).toLowerCase().trim();
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const Section = ({ icon: Icon, title, children }) => (
  <Card className="border-border/60 shadow-warm"><CardContent className="p-6 space-y-4">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1.5">
      <Icon className="h-3 w-3" /> {title}
    </div>
    {children}
  </CardContent></Card>
);

export default function PublicDeclarationRequestForm() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", config: null });
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [captcha, setCaptcha] = useState({ question: "", captcha_token: "" });
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const loadCaptcha = () => {
    // Endpoint renommé /challenge (ex-/captcha) pour éviter les blocages ad-blocker
    axios.get(`${API_BASE}/public-links/${token}/challenge`)
      .then(({ data }) => { setCaptcha(data); setCaptchaAnswer(""); })
      .catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API_BASE}/public-links/${token}/form`)
      .then(({ data }) => {
        if (data.type !== "declaration_request") {
          setState({ loading: false, error: "Ce lien n'est pas un formulaire de demande de déclaration", config: null });
          return;
        }
        setState({ loading: false, error: "", config: data });
        loadCaptcha();
      })
      .catch(e => setState({ loading: false, error: e?.response?.data?.detail || "Lien invalide ou expiré", config: null }));
    // eslint-disable-next-line
  }, [token]);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const preFilled = !!state.config?.client_prefill;
  const prefill = state.config?.client_prefill;
  const intermittentPrefill = state.config?.intermittent_prefill; // Portail intermittent → l'intermittent est verrouillé, pas le client
  const isIntermittentPortal = !!intermittentPrefill;
  const isArtist = (intermittentPrefill?.role_type || "").toLowerCase() === "artiste";
  const isCompanyPortal = preFilled && !isIntermittentPortal;
  const contactLocked = preFilled && !isIntermittentPortal;
  // Portail entreprise : gestion des contacts (ajout d'un nouveau contact)
  const [addingContact, setAddingContact] = useState(false);
  // Portail entreprise : l'artiste choisi côté form force hours_cachet à 12 (default)
  const isFormArtist = form.role_type === "artiste";

  useEffect(() => {
    if (prefill && !isIntermittentPortal) {
      setForm(f => ({ ...f, client_id: prefill.id || "", company_name: prefill.name || "",
                            contact_first_name: prefill.contact_first_name || "",
                            contact_last_name: prefill.contact_last_name || "",
                            contact_email: prefill.contact_email || "",
                            contact_phone: prefill.contact_phone || "",
                            contact_role: prefill.contact_role || "" }));
    }
  }, [prefill, isIntermittentPortal]);

  // Auto-sélection du contact unique : si le client n'a qu'un seul contact, on le pré-remplit automatiquement
  useEffect(() => {
    if (!isCompanyPortal) return;
    if (addingContact) return;
    const list = gatherContacts(state.config?.client_prefill);
    if (list.length === 1 && !form.contact_email) {
      const c = list[0];
      setForm(f => ({
        ...f,
        contact_first_name: c.first_name || "",
        contact_last_name: c.last_name || "",
        contact_email: c.email || "",
        contact_phone: c.phone || "",
        contact_role: c.role || "",
      }));
    }
    // eslint-disable-next-line
  }, [state.config?.client_prefill, isCompanyPortal, addingContact]);

  // Auto-fill 12h pour un artiste (portail intermittent)
  useEffect(() => {
    if (isArtist) {
      setForm(f => (parseFloat(f.hours_cachet) > 0 ? f : { ...f, hours_cachet: 12 }));
    }
  }, [isArtist]);

  // Portail entreprise : quand on choisit "artiste" sur une ligne intermittent, préremplir hours_cachet=12
  // (géré directement dans setRowRoleType). Cet effect gère uniquement le portail intermittent legacy.
  useEffect(() => { /* placeholder — remplace anciens effets */ }, [form.role_type, isCompanyPortal]);

  // ---- Multi-intermittents (portail entreprise) ----
  const updateRow = (idx, patch) => setForm(f => {
    const arr = [...(f.intermittents || [])];
    arr[idx] = { ...arr[idx], ...patch };
    return { ...f, intermittents: arr };
  });
  const onRowNameChange = (idx, val) => {
    const norm = (val || "").trim().toLowerCase();
    const match = (state.config?.intermittents || []).find(im => fmtIntermittent(im).toLowerCase() === norm);
    setForm(f => {
      const arr = [...(f.intermittents || [])];
      const cur = arr[idx] || { ...EMPTY_INTERMITTENT };
      arr[idx] = {
        ...cur,
        intermittent_name_input: val,
        intermittent_id: match?.id || "",
        // Auto-remplissage du poste s'il est vide et que l'intermittent a un rôle en base
        role: (!cur.role && match?.role) ? match.role : cur.role,
      };
      return { ...f, intermittents: arr };
    });
  };
  const setRowRoleType = (idx, rt) => setForm(f => {
    const arr = [...(f.intermittents || [])];
    const cur = arr[idx] || { ...EMPTY_INTERMITTENT };
    let nextHours = cur.hours_cachet;
    if (rt === "artiste") {
      if (!(parseFloat(cur.hours_cachet) > 0)) nextHours = 12;
    } else {
      // Passage sur technicien : si l'ancien était 12 (default artiste), on vide
      if (parseFloat(cur.hours_cachet) === 12) nextHours = "";
    }
    arr[idx] = { ...cur, role_type: rt, hours_cachet: nextHours };
    return { ...f, intermittents: arr };
  });
  const addIntermittent = () => setForm(f => ({ ...f, intermittents: [...(f.intermittents || []), { ...EMPTY_INTERMITTENT }] }));
  const duplicateIntermittent = (idx) => setForm(f => {
    const arr = [...(f.intermittents || [])];
    const src = arr[idx] || EMPTY_INTERMITTENT;
    // Copie tout SAUF le nom (pour forcer la saisie du nouvel intermittent)
    const clone = { ...src, intermittent_id: "", intermittent_name_input: "" };
    arr.splice(idx + 1, 0, clone);
    return { ...f, intermittents: arr };
  });
  const removeIntermittent = (idx) => setForm(f => {
    const arr = [...(f.intermittents || [])];
    arr.splice(idx, 1);
    return { ...f, intermittents: arr.length ? arr : [{ ...EMPTY_INTERMITTENT }] };
  });

  // Client courant (selon client_id sélectionné) → contacts disponibles
  const currentClient = state.config?.clients?.find(c => c.id === form.client_id) || (prefill && !isIntermittentPortal ? prefill : null);
  const availableContacts = gatherContacts(currentClient);

  const applyContact = (c) => {
    if (!c) return;
    setForm(f => ({
      ...f,
      contact_first_name: c.first_name || "",
      contact_last_name: c.last_name || "",
      contact_email: c.email || "",
      contact_phone: c.phone || "",
      contact_role: c.role || "",
    }));
  };

  const onClientChange = (id) => {
    const c = state.config?.clients?.find(x => x.id === id);
    set({ client_id: id, company_name: c?.name || "" });
  };

  // Sélection d'un client dans le datalist (intermittent portal) → auto-fill contact
  const onCompanyNameInput = (val) => {
    const match = state.config?.clients?.find(c => c.name?.toLowerCase() === val.toLowerCase());
    if (match) {
      setForm(f => ({
        ...f,
        company_name: match.name,
        client_id: match.id,
        contact_last_name: f.contact_last_name || match.contact_last_name || "",
        contact_first_name: f.contact_first_name || match.contact_first_name || "",
        contact_email: f.contact_email || match.contact_email || "",
        contact_phone: f.contact_phone || match.contact_phone || "",
        contact_role: f.contact_role || match.contact_role || "",
      }));
    } else {
      // Utilisateur retape par-dessus un match précédent → vider les contact_* auto-remplis
      setForm(f => {
        const wasAutoFilled = !!f.client_id;
        return {
          ...f,
          company_name: val,
          client_id: "",
          contact_last_name: wasAutoFilled ? "" : f.contact_last_name,
          contact_first_name: wasAutoFilled ? "" : f.contact_first_name,
          contact_email: wasAutoFilled ? "" : f.contact_email,
          contact_phone: wasAutoFilled ? "" : f.contact_phone,
          contact_role: wasAutoFilled ? "" : f.contact_role,
        };
      });
    }
  };

  const onEventNameChange = (name) => {
    const match = state.config?.events?.find(e => e.name?.toLowerCase() === name.toLowerCase());
    setForm(prev => ({ ...prev, event_name: name, event_location: match?.location ? match.location : prev.event_location }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const required = ["contact_first_name", "contact_last_name", "contact_email", "contact_phone", "contact_role",
                       "start_date", "end_date", "event_name", "event_location"];
    for (const f of required) {
      if (!form[f] || String(form[f]).trim() === "") { toast.error(`Champ requis manquant`); return; }
    }
    if (!form.client_id && !form.company_name) { toast.error("Sélectionnez ou saisissez un client"); return; }
    if (isCompanyPortal) {
      const rows = form.intermittents || [];
      if (rows.length === 0) { toast.error("Ajoutez au moins un intermittent"); return; }
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        if (!r.role_type) { toast.error(`Intermittent #${i + 1} : type requis (Technicien / Artiste)`); return; }
        if (!(r.role || "").trim()) { toast.error(`Intermittent #${i + 1} : poste requis`); return; }
        if (!(parseInt(r.cachet_count) > 0)) { toast.error(`Intermittent #${i + 1} : nombre de cachets requis (min. 1)`); return; }
        if (!(parseFloat(r.hours_cachet) > 0)) { toast.error(`Intermittent #${i + 1} : durée d'un cachet requise (> 0h)`); return; }
        if (!(r.start_time || "").trim()) { toast.error(`Intermittent #${i + 1} : heure de prise de poste requise`); return; }
        if (r.expenses_expected !== true && r.expenses_expected !== false) { toast.error(`Intermittent #${i + 1} : indiquez si des frais sont à prévoir (Oui/Non)`); return; }
        if (!(parseFloat(r.cost_ht_client) > 0)) { toast.error(`Intermittent #${i + 1} : coût HT requis (> 0)`); return; }
      }
    } else {
      if (!(parseInt(form.cachet_count) > 0)) { toast.error("Nombre de cachets requis (min. 1)"); return; }
      if (!(parseFloat(form.hours_cachet) > 0)) { toast.error("Durée d'un cachet requise (> 0h)"); return; }
      if (!(form.start_time || "").trim()) { toast.error("Heure de prise de poste requise"); return; }
      if (form.expenses_expected !== true && form.expenses_expected !== false) { toast.error("Indiquez si des frais sont à prévoir (Oui/Non)"); return; }
      if (!(parseFloat(form.cost_ht_client) > 0)) { toast.error("Montant HT requis"); return; }
    }
    if (!state.config?.disable_captcha) {
      if (!captchaAnswer.trim()) { toast.error("Répondez à la question anti-robot"); return; }
    }

    setSubmitting(true);
    try {
      // Somme totale HT (portail entreprise) ou coût unique (autres portails)
      const totalHt = isCompanyPortal
        ? (form.intermittents || []).reduce((s, r) => s + (parseFloat(r.cost_ht_client) || 0), 0)
        : (parseFloat(form.cost_ht_client) || 0);
      const payload = {
        client_id: form.client_id || null,
        company_name: form.company_name || "",
        contact_first_name: form.contact_first_name,
        contact_last_name: form.contact_last_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        contact_role: form.contact_role,
        event_name: form.event_name,
        event_location: form.event_location,
        start_date: form.start_date,
        end_date: form.end_date,
        start_time: isCompanyPortal ? (form.intermittents?.[0]?.start_time || "") : (form.start_time || ""),
        // Multi-intermittents (portail entreprise uniquement) — inclut coût/frais/heure/notes par ligne
        intermittents: isCompanyPortal
          ? (form.intermittents || []).map(it => ({
              intermittent_id: it.intermittent_id || null,
              role: it.role || "",
              role_type: it.role_type || "technicien",
              cachet_count: parseInt(it.cachet_count) || 1,
              hours_cachet: parseFloat(it.hours_cachet) || 0,
              cost_ht_client: parseFloat(it.cost_ht_client) || 0,
              expenses_expected: it.expenses_expected,
              fees_included: !!it.fees_included,
              start_time: it.start_time || "",
              notes: it.notes || "",
            }))
          : [],
        // Legacy — pour intermittent portal / lien libre
        intermittent_id: isCompanyPortal ? (form.intermittents?.[0]?.intermittent_id || null) : (form.intermittent_id || null),
        role: isCompanyPortal ? (form.intermittents?.[0]?.role || "") : (form.role || ""),
        role_type: isCompanyPortal ? (form.intermittents?.[0]?.role_type || "technicien") : (form.role_type || ""),
        cachet_count: isCompanyPortal ? (parseInt(form.intermittents?.[0]?.cachet_count) || 1) : (parseInt(form.cachet_count) || 1),
        hours_cachet: isCompanyPortal ? (parseFloat(form.intermittents?.[0]?.hours_cachet) || 0) : (parseFloat(form.hours_cachet) || 0),
        cost_ht_client: totalHt,
        fees_included: !!form.fees_included,
        expenses_expected: form.expenses_expected,
        contract_expenses: (form.contract_expenses || [])
          .map(e => ({ title: (e.title || "").trim(), category: e.category || "", amount: parseFloat(e.amount) || 0 }))
          .filter(e => e.title && e.amount > 0),
        notes: form.notes,
        captcha_answer: state.config?.disable_captcha ? "" : captchaAnswer.trim(),
        captcha_token: state.config?.disable_captcha ? "" : captcha.captcha_token,
      };
      await axios.post(`${API_BASE}/public-links/${token}/request-declaration`, payload);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi");
      loadCaptcha();
    } finally { setSubmitting(false); }
  };

  if (state.loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Chargement...</div>;
  if (state.error) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Card className="max-w-md w-full border-border/60 shadow-warm-lg"><CardContent className="p-8 text-center">
        <div className="text-destructive text-4xl mb-3">✕</div>
        <h2 className="font-brand text-2xl mb-2">Lien inaccessible</h2>
        <p className="text-muted-foreground text-sm">{state.error}</p>
      </CardContent></Card>
    </div>
  );
  if (done) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Card className="max-w-md w-full border-border/60 shadow-warm-lg"><CardContent className="p-8 text-center">
        <CheckCircle2 className="h-16 w-16 text-primary mx-auto mb-4" />
        <h2 className="font-brand text-3xl mb-2">Demande envoyée</h2>
        <p className="text-muted-foreground">Votre demande de déclaration a été transmise à l'équipe Antoine Événements.</p>
        <p className="text-xs text-muted-foreground mt-6">Nous préparons le contrat et revenons vers vous rapidement.</p>
      </CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen">
      <div className="border-b border-border/60 bg-card">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={LOGO} alt="Antoine Événements" className="h-10 w-auto" />
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Demande de déclaration</div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">Client · demande de contrat</div>
          <h1 className="font-brand text-3xl md:text-4xl tracking-tight">Formuler une demande de déclaration</h1>
          {state.config?.label && <p className="text-sm text-muted-foreground mt-2">{state.config.label}</p>}
          <p className="text-xs text-muted-foreground mt-2">Tous les champs marqués d'un * sont obligatoires.</p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="request-form">
          {isIntermittentPortal && (
            <Card className="border-primary/40 bg-primary/5 shadow-warm">
              <CardContent className="p-5 flex items-center gap-3">
                <UserCircle2 className="h-8 w-8 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-primary font-bold">Demande au nom de</div>
                  <div className="font-semibold text-lg leading-tight">{`${intermittentPrefill.last_name?.toUpperCase() || ""} ${intermittentPrefill.first_name || ""}`.trim()}</div>
                  {intermittentPrefill.role && <div className="text-xs text-muted-foreground mt-0.5">{intermittentPrefill.role}</div>}
                  <div className="text-[11px] text-muted-foreground mt-1">Le contrat sera automatiquement à votre nom. Renseignez ci-dessous le client à facturer.</div>
                </div>
              </CardContent>
            </Card>
          )}

          <Section icon={Building2} title="Client à facturer">
            {preFilled && !isIntermittentPortal ? (
              <div>
                <Label>Nom du client à facturer</Label>
                <Input value={prefill?.name || ""} readOnly className="bg-muted" data-testid="req-company-locked" />
                <div className="text-[11px] text-muted-foreground mt-1">Verrouillé — lien dédié à votre compte.</div>
              </div>
            ) : isIntermittentPortal ? (
              <div>
                <Label>Nom du client à facturer *</Label>
                <Input
                  list="clients-suggest"
                  value={form.company_name}
                  onChange={e => onCompanyNameInput(e.target.value)}
                  placeholder="Tapez les premières lettres — sélectionnez un client existant ou saisissez un nouveau nom"
                  data-testid="req-company-name"
                  autoComplete="off"
                />
                <datalist id="clients-suggest">
                  {(state.config?.clients || []).map(c => <option key={c.id} value={c.name} />)}
                </datalist>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {form.client_id
                    ? "✓ Client existant — les infos contact sont préremplies (modifiables)."
                    : (form.company_name?.trim() ? "Nouveau client — il sera créé automatiquement (type entreprise, marge 0%)." : "Sélectionnez dans la liste ou saisissez un nouveau nom.")}
                </div>
              </div>
            ) : (
              <div>
                <Label>Nom du client à facturer *</Label>
                <Select value={form.client_id || ""} onValueChange={onClientChange}>
                  <SelectTrigger data-testid="req-client"><SelectValue placeholder={state.config?.clients?.length ? "Sélectionner un client existant..." : "Aucun client enregistré — saisissez le nom"} /></SelectTrigger>
                  <SelectContent>
                    {(state.config?.clients || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.kind === "intermittent" ? " (intermittent)" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                {(!state.config?.clients?.length || !form.client_id) && (
                  <Input className="mt-2" placeholder="Ou saisissez le nom du client" value={form.company_name} onChange={e => set({ company_name: e.target.value })} data-testid="req-company-name" />
                )}
              </div>
            )}
          </Section>

          <Section icon={UserCircle2} title="Contact du client">
            {isCompanyPortal && availableContacts.length >= 1 && !addingContact && (
              <div className="mb-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
                <Label className="text-[11px] uppercase tracking-widest text-primary font-bold">Contacts enregistrés pour {prefill?.name}</Label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="req-contact-picker">
                  {availableContacts.map((c, i) => {
                    const active = c.email && form.contact_email && c.email.toLowerCase() === form.contact_email.toLowerCase();
                    return (
                      <button
                        type="button"
                        key={i}
                        onClick={() => applyContact(c)}
                        data-testid={`req-contact-option-${i}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition active:scale-95 ${active ? "border-primary bg-primary text-primary-foreground" : "border-primary/40 bg-card text-primary hover:bg-primary/10"}`}
                      >
                        {(c.last_name || "").toUpperCase()} {c.first_name}
                        {c.role && <span className="ml-1 opacity-70 font-normal">· {c.role}</span>}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => { setAddingContact(true); setForm(f => ({ ...f, contact_first_name: "", contact_last_name: "", contact_email: "", contact_phone: "", contact_role: "" })); }}
                    data-testid="req-add-contact-btn"
                    className="px-3 py-1.5 rounded-full text-xs font-semibold border-2 border-dashed border-primary/40 text-primary bg-card hover:bg-primary/5 transition active:scale-95 flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" /> Ajouter un contact
                  </button>
                </div>
                <div className="text-[11px] text-muted-foreground mt-2">Cliquez sur un contact existant, ou ajoutez-en un nouveau.</div>
              </div>
            )}
            {!isCompanyPortal && availableContacts.length > 1 && !contactLocked && (
              <div className="mb-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
                <Label className="text-[11px] uppercase tracking-widest text-primary font-bold">Contacts enregistrés pour ce client</Label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="req-contact-picker">
                  {availableContacts.map((c, i) => {
                    const active = c.email && form.contact_email && c.email.toLowerCase() === form.contact_email.toLowerCase();
                    return (
                      <button
                        type="button"
                        key={i}
                        onClick={() => applyContact(c)}
                        data-testid={`req-contact-option-${i}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition active:scale-95 ${active ? "border-primary bg-primary text-primary-foreground" : "border-primary/40 bg-card text-primary hover:bg-primary/10"}`}
                      >
                        {(c.last_name || "").toUpperCase()} {c.first_name}
                        {c.role && <span className="ml-1 opacity-70 font-normal">· {c.role}</span>}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[11px] text-muted-foreground mt-2">Cliquez pour préremplir tous les champs — modifiable ensuite.</div>
              </div>
            )}
            {isCompanyPortal && addingContact && (
              <div className="mb-3 flex items-center justify-between rounded-lg border border-secondary/40 bg-secondary/5 p-2 px-3 text-xs">
                <span className="text-secondary-foreground font-semibold">Nouveau contact — les champs sont modifiables</span>
                <button type="button" onClick={() => setAddingContact(false)} className="text-primary underline">Choisir un existant</button>
              </div>
            )}
            {(() => {
              const locked = isCompanyPortal ? (!addingContact && !!form.contact_email) : contactLocked;
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nom *</Label>
                    <Input required list={availableContacts.length ? "contacts-lastname" : undefined} readOnly={locked} className={locked ? "bg-muted" : ""} value={form.contact_last_name} onChange={e => set({ contact_last_name: e.target.value })} data-testid="req-contact-last" autoComplete="off" />
                    {availableContacts.length > 0 && (
                      <datalist id="contacts-lastname">
                        {availableContacts.map((c, i) => <option key={i} value={c.last_name || ""} />)}
                      </datalist>
                    )}
                  </div>
                  <div><Label>Prénom *</Label><Input required readOnly={locked} className={locked ? "bg-muted" : ""} value={form.contact_first_name} onChange={e => set({ contact_first_name: e.target.value })} data-testid="req-contact-first" /></div>
                  <div>
                    <Label>Email *</Label>
                    <Input required list={availableContacts.length ? "contacts-email" : undefined} readOnly={locked} className={locked ? "bg-muted" : ""} type="email" value={form.contact_email} onChange={e => set({ contact_email: e.target.value })} data-testid="req-contact-email" autoComplete="off" />
                    {availableContacts.length > 0 && (
                      <datalist id="contacts-email">
                        {availableContacts.map((c, i) => <option key={i} value={c.email || ""} />)}
                      </datalist>
                    )}
                  </div>
                  <div><Label>Téléphone *</Label><Input required readOnly={locked} className={locked ? "bg-muted" : ""} value={form.contact_phone} onChange={e => set({ contact_phone: e.target.value })} data-testid="req-contact-phone" /></div>
                  <div className="md:col-span-2"><Label>Poste *</Label><Input required readOnly={locked} className={locked ? "bg-muted" : ""} value={form.contact_role} onChange={e => set({ contact_role: e.target.value })} placeholder="Ex : Chargé de production, Responsable événementiel..." data-testid="req-contact-role" /></div>
                </div>
              );
            })()}
          </Section>

          {/* Intermittents à déclarer — déplacé APRÈS Événement (voir plus bas). */}

          <Section icon={CalendarPlus} title="Événement">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Nom de l'événement *</Label>
                <Input required list="ev-suggest-req" value={form.event_name} onChange={e => onEventNameChange(e.target.value)} data-testid="req-event" autoComplete="off" placeholder="Commencez à saisir — suggestions basées sur vos déclarations passées" />
                <datalist id="ev-suggest-req">
                  {(state.config?.event_name_suggestions || state.config?.events?.map(e => e.name) || []).map((name, i) => (
                    <option key={`ev-${i}`} value={name} />
                  ))}
                </datalist>
              </div>
              <div className="md:col-span-2">
                <Label>Lieu de l'événement *</Label>
                <Input required list="loc-suggest-req" value={form.event_location} onChange={e => set({ event_location: e.target.value })} data-testid="req-location" autoComplete="off" placeholder="Commencez à saisir — suggestions basées sur les lieux déjà utilisés" />
                <datalist id="loc-suggest-req">
                  {(state.config?.location_suggestions || []).map((loc, i) => (
                    <option key={`loc-${i}`} value={loc} />
                  ))}
                </datalist>
              </div>
              <div><Label>Date de début *</Label><Input type="date" required value={form.start_date} onChange={e => set({ start_date: e.target.value })} data-testid="req-start" /></div>
              <div><Label>Date de fin *</Label><Input type="date" required value={form.end_date} onChange={e => set({ end_date: e.target.value })} data-testid="req-end" /></div>
              {!isCompanyPortal && (
                <div className="md:col-span-2">
                  <Label>Heure de prise de poste</Label>
                  <Input type="time" value={form.start_time} onChange={e => set({ start_time: e.target.value })} data-testid="req-start-time" />
                  <div className="text-[11px] text-muted-foreground mt-1">Optionnel — sera repris dans la fiche du contrat côté administration.</div>
                </div>
              )}
              {isCompanyPortal && (
                <div className="md:col-span-2">
                  <Label>Notes complémentaires</Label>
                  <Textarea rows={3} value={form.notes} onChange={e => set({ notes: e.target.value })} placeholder="Précisions sur la prestation, contraintes particulières..." data-testid="req-notes" />
                </div>
              )}
            </div>
          </Section>

          {isCompanyPortal && (
            <Section icon={Users} title="Intermittents à déclarer">
              <div className="space-y-4">
                {(form.intermittents || []).map((row, idx) => (
                  <div key={idx} className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3 relative" data-testid={`req-intermittent-row-${idx}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-widest text-primary font-bold">Intermittent #{idx + 1}</div>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => duplicateIntermittent(idx)}
                          data-testid={`req-duplicate-intermittent-${idx}`}
                          className="text-primary text-xs font-semibold hover:underline"
                          title="Dupliquer cet intermittent (nom vidé)"
                        >
                          Dupliquer
                        </button>
                        {(form.intermittents || []).length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeIntermittent(idx)}
                            data-testid={`req-remove-intermittent-${idx}`}
                            className="text-destructive text-xs font-semibold hover:underline"
                          >
                            Retirer
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label>Nom de l'intermittent</Label>
                        <Input
                          list="req-intermittent-list"
                          value={row.intermittent_name_input || ""}
                          onChange={e => onRowNameChange(idx, e.target.value)}
                          placeholder="Tapez les premières lettres — les propositions s'affinent"
                          data-testid={`req-intermittent-name-${idx}`}
                          autoComplete="off"
                        />
                        <div className="text-[11px] text-muted-foreground mt-1">
                          {row.intermittent_id
                            ? "✓ Intermittent identifié"
                            : (row.intermittent_name_input ? "Nom non reconnu — l'AAE assignera l'intermittent." : "Optionnel — laissez vide si l'AAE doit choisir.")}
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <Label>Type</Label>
                        <div className="flex gap-3 mt-1" data-testid={`req-role-type-${idx}`}>
                          <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none ${row.role_type === "technicien" ? "border-primary bg-primary/5 text-primary" : "border-border bg-card"}`}>
                            <input type="radio" name={`role_type_${idx}`} checked={row.role_type === "technicien"} onChange={() => setRowRoleType(idx, "technicien")} data-testid={`req-role-type-tech-${idx}`} />
                            <span className="text-sm font-semibold">Technicien</span>
                          </label>
                          <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none ${row.role_type === "artiste" ? "border-primary bg-primary/5 text-primary" : "border-border bg-card"}`}>
                            <input type="radio" name={`role_type_${idx}`} checked={row.role_type === "artiste"} onChange={() => setRowRoleType(idx, "artiste")} data-testid={`req-role-type-artist-${idx}`} />
                            <span className="text-sm font-semibold">Artiste</span>
                          </label>
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <Label>Poste *</Label>
                        <Input
                          list="req-role-list"
                          required
                          value={row.role || ""}
                          onChange={e => updateRow(idx, { role: e.target.value })}
                          placeholder="Régisseur son, Technicien lumière, Danseur…"
                          data-testid={`req-role-${idx}`}
                          autoComplete="off"
                        />
                      </div>
                      <div>
                        <Label>Nombre de cachets à faire *</Label>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          required
                          value={row.cachet_count}
                          onKeyDown={e => { if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault(); }}
                          onChange={e => updateRow(idx, { cachet_count: e.target.value.replace(/[^0-9]/g, "") })}
                          data-testid={`req-cachet-count-${idx}`}
                        />
                      </div>
                      <div>
                        <Label>Durée d'un cachet (heures) *{row.role_type === "artiste" ? " — 12 par défaut" : ""}</Label>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          required
                          value={row.hours_cachet}
                          onKeyDown={e => { if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault(); }}
                          onChange={e => updateRow(idx, { hours_cachet: e.target.value.replace(/[^0-9]/g, "") })}
                          placeholder={row.role_type === "artiste" ? "12" : "Ex : 8, 10, 12"}
                          data-testid={`req-hours-cachet-${idx}`}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Heure de prise de poste *</Label>
                        <Input
                          type="time"
                          required
                          value={row.start_time || ""}
                          onChange={e => updateRow(idx, { start_time: e.target.value })}
                          data-testid={`req-start-time-${idx}`}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Coût à facturer au client (montant HT) *</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          required
                          value={row.cost_ht_client}
                          onChange={e => updateRow(idx, { cost_ht_client: e.target.value })}
                          data-testid={`req-cost-ht-${idx}`}
                        />
                        <div className="text-[11px] text-muted-foreground mt-1">TVA 20% en sus lors de la facturation.</div>
                      </div>
                      <div className="md:col-span-2">
                        <Label>Est-ce que des frais sont à prévoir pour ce contrat ? *</Label>
                        <div className="flex gap-3 mt-1" data-testid={`req-expenses-expected-${idx}`}>
                          <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none ${row.expenses_expected === true ? "border-primary bg-primary/5 text-primary" : "border-border bg-card"}`}>
                            <input type="radio" name={`expenses_expected_${idx}`} checked={row.expenses_expected === true} onChange={() => updateRow(idx, { expenses_expected: true })} data-testid={`req-expenses-yes-${idx}`} />
                            <span className="text-sm font-semibold">Oui</span>
                          </label>
                          <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none ${row.expenses_expected === false ? "border-primary bg-primary/5 text-primary" : "border-border bg-card"}`}>
                            <input type="radio" name={`expenses_expected_${idx}`} checked={row.expenses_expected === false} onChange={() => updateRow(idx, { expenses_expected: false })} data-testid={`req-expenses-no-${idx}`} />
                            <span className="text-sm font-semibold">Non</span>
                          </label>
                        </div>
                      </div>
                      {row.expenses_expected === true && (
                        <div className="md:col-span-2">
                          <Label>Imputation des frais</Label>
                          <div className="flex gap-3 mt-1" data-testid={`req-fees-${idx}`}>
                            <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none ${row.fees_included ? "border-primary bg-primary/5 text-primary" : "border-border bg-card"}`}>
                              <input type="radio" name={`fees_${idx}`} checked={row.fees_included} onChange={() => updateRow(idx, { fees_included: true })} data-testid={`req-fees-included-${idx}`} />
                              <span className="text-sm font-semibold">Frais inclus dans le montant à facturer</span>
                            </label>
                            <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none ${!row.fees_included ? "border-primary bg-primary/5 text-primary" : "border-border bg-card"}`}>
                              <input type="radio" name={`fees_${idx}`} checked={!row.fees_included} onChange={() => updateRow(idx, { fees_included: false })} data-testid={`req-fees-excluded-${idx}`} />
                              <span className="text-sm font-semibold">Frais facturés en sus</span>
                            </label>
                          </div>
                        </div>
                      )}
                      <div className="md:col-span-2">
                        <Label>Notes complémentaires</Label>
                        <Textarea
                          rows={3}
                          value={row.notes || ""}
                          onChange={e => updateRow(idx, { notes: e.target.value })}
                          placeholder="Précisions spécifiques à cet intermittent (contraintes, matériel, briefing…)"
                          data-testid={`req-notes-${idx}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <datalist id="req-intermittent-list">
                  {(state.config?.intermittents || []).map(im => (
                    <option key={im.id} value={fmtIntermittent(im)}>{im.role || ""}</option>
                  ))}
                </datalist>
                <datalist id="req-role-list">
                  {(state.config?.role_suggestions || []).map((r, i) => <option key={i} value={r} />)}
                </datalist>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full border-primary/40 text-primary hover:bg-primary/10"
                  onClick={addIntermittent}
                  data-testid="req-add-intermittent"
                >
                  <Plus className="h-4 w-4 mr-2" /> Ajouter un intermittent
                </Button>
                <div className="text-[11px] text-muted-foreground italic">
                  Tous les intermittents ajoutés partageront le <strong>même événement</strong> et les <strong>mêmes dates</strong>. Une déclaration distincte sera créée pour chacun avec son propre coût et frais.
                </div>
              </div>
            </Section>
          )}

          {!isCompanyPortal && (
          <Section icon={Euro} title="Prestation & facturation">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {!isCompanyPortal && (
                <>
                  <div>
                    <Label>Nombre de cachets à faire *</Label>
                    <Input type="number" min="1" step="1" required value={form.cachet_count} onChange={e => set({ cachet_count: e.target.value })} data-testid="req-cachet-count" />
                    <div className="text-[11px] text-muted-foreground mt-1">Nombre de missions à déclarer</div>
                  </div>
                  <div>
                    <Label>Durée d'un cachet (heures){isArtist ? " (artiste : 12h par défaut)" : ""}</Label>
                    <Input type="number" min="0" step="0.5" value={form.hours_cachet} onChange={e => set({ hours_cachet: e.target.value })} data-testid="req-hours-cachet" placeholder={isArtist ? "12" : "Ex : 8, 10, 12"} />
                    <div className="text-[11px] text-muted-foreground mt-1">
                      {isArtist ? "Rempli automatiquement à 12h pour un artiste — modifiable." : "Laissez à 0 si non défini (défaut : 12h artiste, 0h technicien)"}
                    </div>
                  </div>
                </>
              )}
              <div className="md:col-span-2">
                <Label>Coût à facturer au client (montant HT) *</Label>
                <Input type="number" min="0" step="0.01" required value={form.cost_ht_client} onChange={e => set({ cost_ht_client: e.target.value })} data-testid="req-ht" />
                <div className="text-[11px] text-muted-foreground mt-1">TVA 20% en sus lors de la facturation</div>
              </div>
              <div className="md:col-span-2">
                <Label>Est-ce que des frais sont à prévoir pour ce contrat ?</Label>
                <div className="flex gap-3 mt-1" data-testid="req-expenses-expected">
                  <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none ${form.expenses_expected === true ? "border-primary bg-primary/5 text-primary" : "border-border"}`}>
                    <input type="radio" name="expenses_expected" checked={form.expenses_expected === true} onChange={() => set({ expenses_expected: true })} data-testid="req-expenses-yes" />
                    <span className="text-sm font-semibold">Oui</span>
                  </label>
                  <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none ${form.expenses_expected === false ? "border-primary bg-primary/5 text-primary" : "border-border"}`}>
                    <input type="radio" name="expenses_expected" checked={form.expenses_expected === false} onChange={() => set({ expenses_expected: false })} data-testid="req-expenses-no" />
                    <span className="text-sm font-semibold">Non</span>
                  </label>
                </div>
              </div>
              {form.expenses_expected === true && (
                <div className="md:col-span-2">
                  <Label>Imputation des frais</Label>
                  <div className="flex gap-3 mt-1" data-testid="req-fees">
                    <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none ${form.fees_included ? "border-primary bg-primary/5 text-primary" : "border-border"}`}>
                      <input type="radio" name="fees" checked={form.fees_included} onChange={() => set({ fees_included: true })} data-testid="req-fees-included" />
                      <span className="text-sm font-semibold">Frais inclus dans le montant à facturer</span>
                    </label>
                    <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none ${!form.fees_included ? "border-primary bg-primary/5 text-primary" : "border-border"}`}>
                      <input type="radio" name="fees" checked={!form.fees_included} onChange={() => set({ fees_included: false })} data-testid="req-fees-excluded" />
                      <span className="text-sm font-semibold">Frais facturés en sus</span>
                    </label>
                  </div>
                </div>
              )}
              {form.expenses_expected === true && (
                <div className="md:col-span-2 border-t border-border/50 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[11px] uppercase tracking-widest text-primary font-bold">Liste des frais prévus</Label>
                    <Button
                      type="button" variant="outline" size="sm" className="rounded-full h-8"
                      onClick={() => set({ contract_expenses: [...(form.contract_expenses || []), { title: "", category: "", amount: "" }] })}
                      data-testid="req-add-expense"
                    >+ Ajouter un frais</Button>
                  </div>
                  <div className="space-y-2" data-testid="req-expenses-list">
                    {(form.contract_expenses || []).length === 0 && (
                      <div className="text-xs text-muted-foreground italic">Aucun frais renseigné pour le moment. Cliquez sur « Ajouter un frais » pour en déclarer.</div>
                    )}
                    {(form.contract_expenses || []).map((exp, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 rounded-lg border border-border/60 bg-muted" data-testid={`req-expense-row-${i}`}>
                        <div className="col-span-12 md:col-span-5">
                          <Label className="text-[10px]">Nom du frais *</Label>
                          <Input value={exp.title} onChange={e => {
                            const arr = [...form.contract_expenses]; arr[i] = { ...arr[i], title: e.target.value }; set({ contract_expenses: arr });
                          }} placeholder="Ex : Ticket TGV Paris-Lyon" data-testid={`req-expense-title-${i}`} />
                        </div>
                        <div className="col-span-8 md:col-span-4">
                          <Label className="text-[10px]">Catégorie</Label>
                          <select value={exp.category} onChange={e => {
                            const arr = [...form.contract_expenses]; arr[i] = { ...arr[i], category: e.target.value }; set({ contract_expenses: arr });
                          }} className="w-full h-10 border border-border rounded-md px-3 bg-background text-sm" data-testid={`req-expense-cat-${i}`}>
                            <option value="">— Choisir —</option>
                            {EXPENSE_CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
                          </select>
                        </div>
                        <div className="col-span-3 md:col-span-2">
                          <Label className="text-[10px]">Montant (€)</Label>
                          <Input type="number" min="0" step="0.01" value={exp.amount} onChange={e => {
                            const arr = [...form.contract_expenses]; arr[i] = { ...arr[i], amount: e.target.value }; set({ contract_expenses: arr });
                          }} data-testid={`req-expense-amount-${i}`} />
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive hover:bg-destructive/10" onClick={() => {
                            const arr = [...form.contract_expenses]; arr.splice(i, 1); set({ contract_expenses: arr });
                          }} data-testid={`req-expense-del-${i}`}>×</Button>
                        </div>
                      </div>
                    ))}
                    {(form.contract_expenses || []).length > 0 && (
                      <div className="text-right text-xs">
                        <span className="text-muted-foreground">Total frais&nbsp;: </span>
                        <span className="font-brand text-lg text-primary">
                          {(form.contract_expenses || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                        </span>
                        <span className="text-muted-foreground ml-1">— {form.fees_included ? "inclus dans le HT" : "en sus du HT"}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="md:col-span-2"><Label>Notes complémentaires</Label><Textarea rows={3} value={form.notes} onChange={e => set({ notes: e.target.value })} placeholder="Précisions sur la prestation, contraintes particulières..." data-testid="req-notes" /></div>
            </div>
          </Section>
          )}

          {!state.config?.disable_captcha && (
          <Section icon={ShieldCheck} title="Vérification anti-robot">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
              <div>
                <Label>{captcha.question || "Chargement..."} *</Label>
                <Input type="text" inputMode="numeric" value={captchaAnswer} onChange={e => setCaptchaAnswer(e.target.value)} placeholder="Votre réponse" required data-testid="req-captcha" />
              </div>
              <div>
                <Button type="button" variant="outline" className="rounded-full" onClick={loadCaptcha} data-testid="req-captcha-refresh"><RefreshCw className="h-4 w-4 mr-2" /> Nouvelle question</Button>
              </div>
            </div>
          </Section>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={submitting} className="rounded-full h-12 px-8 bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-glow active:scale-95" data-testid="req-submit">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              {submitting ? "Envoi..." : "Envoyer la demande"}
            </Button>
          </div>
        </form>

        <div className="text-center text-xs text-muted-foreground mt-8">
          Agence Antoine Événements · <a href="https://www.antoineevenements.fr" target="_blank" rel="noreferrer" className="text-primary underline">antoineevenements.fr</a>
        </div>
      </div>
    </div>
  );
}
